import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

import TerminalHeader from './TradingTerminal/TerminalHeader';
import CoinSelectorModal from './TradingTerminal/CoinSelectorModal';
import PnLCalendarModal from './TradingTerminal/PnLCalendarModal';
import ChartSection from './TradingTerminal/ChartSection';
import OrderEntry from './TradingTerminal/OrderEntry';
import PositionsPanel from './TradingTerminal/PositionsPanel';

import { 
  fetchCryptosAPI, 
  fetchPortfolioAPI, 
  fetchOrdersAPI, 
  triggerLimitOrderAPI, 
  triggerSLTPAPI, 
  placeOrderAPI, 
  cancelOrderAPI, 
  closePositionAPI, 
  updateSLTPAPI, 
  resetAccountAPI 
} from '../services/tradingApi';

const getBaseSymbol = (sym) => {
  if (!sym) return '';
  return sym.replace('USDT', '').replace('USDC', '').toLowerCase();
};

// Convert coin symbol to Binance style (e.g. btc -> BTCUSDT)
const getBinanceSymbol = (sym) => {
  if (!sym) return '';
  const s = sym.toUpperCase();
  if (s === 'USDT') return 'USDTUSDC';
  return `${s}USDT`;
};

const TradingTerminal = () => {
  const { user, logout, token } = useAuth();
  const authHeaders = useMemo(() => token ? { headers: { Authorization: `Bearer ${token}` } } : {}, [token]);
  // Lists and Core States
  const [cryptos, setCryptos] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [timeframe, setTimeframe] = useState('5m');
  const [portfolio, setPortfolio] = useState({ balance: 10000, holdings: [] });
  const [orders, setOrders] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [livePrice, setLivePrice] = useState(0);

  // Animation / Flashing states for sidebar prices
  const prevPricesRef = useRef({});
  const [priceFlash, setPriceFlash] = useState({});

  // Loading States
  const [loading, setLoading] = useState(true);
  const [tradeLoading, setTradeLoading] = useState(false);

  // UI States
  const [isCoinSelectorOpen, setIsCoinSelectorOpen] = useState(false);
  const [isPnLCalendarOpen, setIsPnLCalendarOpen] = useState(false);

  // Keyboard shortcut to open symbol search
  // Alt + S -> open CoinSelectorModal 
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore shortcuts if the user is typing in an input field
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      if (e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        setIsCoinSelectorOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Tab State: 'positions' | 'orders' | 'history'
  const [activeTab, setActiveTab] = useState('positions');

  // Chart feature states
  const [chartType, setChartType] = useState('Candlestick');
  const [showVolume, setShowVolume] = useState(true);
  const [activeIndicators, setActiveIndicators] = useState({ SMA: false, EMA: false, RSI: false, BollingerBands: false });

  // Trade Form States
  const [side, setSide] = useState('BUY'); // BUY or SELL
  const [orderType, setOrderType] = useState('MARKET'); // MARKET or LIMIT
  const [limitPrice, setLimitPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [useSLTP, setUseSLTP] = useState(false);
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  
  // Success/Error Statuses
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Inline SL/TP edit state
  const [editingSLTP, setEditingSLTP] = useState(null); // coinId of holding being edited
  const [editSLVal, setEditSLVal] = useState('');
  const [editTPVal, setEditTPVal] = useState('');

  // Prevent auto-trigger immediately after SL/TP is saved
  const recentlyUpdatedSLTP = useRef(new Set());
  // Prevent duplicate order/SL/TP triggers while API call is in-flight
  const triggeringInFlight = useRef(new Set());

  // Fetch initial data
  const fetchCryptos = useCallback(async () => {
    try {
      const { data } = await fetchCryptosAPI(authHeaders);
      setCryptos(data);
      if (data.length > 0) {
        setSelectedCoin(prev => prev || data[0]);
      }
    } catch (error) {
      console.error('Error fetching cryptos:', error);
    }
  }, [authHeaders]);

  const fetchPortfolio = useCallback(async () => {
    try {
      const { data } = await fetchPortfolioAPI(authHeaders);
      setPortfolio(data);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  }, [authHeaders]);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await fetchOrdersAPI(authHeaders);
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [authHeaders]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCryptos(), fetchPortfolio(), fetchOrders()]);
      setLoading(false);
    };
    init();
  }, [fetchCryptos, fetchPortfolio, fetchOrders]);

  // Update limitPrice input default value when selectedCoin or livePrice changes (if MARKET)
  useEffect(() => {
    if (livePrice && orderType === 'MARKET') {
      setLimitPrice(livePrice.toString());
    }
  }, [livePrice, orderType]);

  // Sidebar live prices aggregate websocket stream
  useEffect(() => {
    if (cryptos.length === 0) return;

    const symbols = cryptos.map(c => getBinanceSymbol(c.symbol).toLowerCase()).filter(s => s !== '');
    const streamNames = symbols.map(s => `${s}@ticker`).join('/');
    const wsBaseUrl = process.env.REACT_APP_BINANCE_WS_URL;
    const wsUrl = `${wsBaseUrl}/stream?streams=${streamNames}`;
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const data = msg.data;
        const binanceSym = data.s; // e.g. BTCUSDT
        const rawPrice = parseFloat(data.c); // Last close price

        // Map back to coin symbol
        const coin = cryptos.find(c => getBinanceSymbol(c.symbol) === binanceSym);
        if (coin) {
          const coinSym = coin.symbol.toLowerCase();
          const prevPrice = prevPricesRef.current[coinSym];

          if (prevPrice !== undefined) {
            if (rawPrice > prevPrice) {
              setPriceFlash(prev => ({ ...prev, [coinSym]: 'flash-up' }));
            } else if (rawPrice < prevPrice) {
              setPriceFlash(prev => ({ ...prev, [coinSym]: 'flash-down' }));
            }
            // Clear flash animation after 400ms
            setTimeout(() => {
              setPriceFlash(prev => ({ ...prev, [coinSym]: '' }));
            }, 400);
          }

          prevPricesRef.current[coinSym] = rawPrice;
          setLivePrices(prev => ({ ...prev, [coinSym]: rawPrice }));

          if (selectedCoin && selectedCoin.symbol.toLowerCase() === coinSym) {
            setLivePrice(rawPrice);
          }
        }
      } catch (e) {
        console.error('Aggregate WebSocket parse error:', e);
      }
    };

    ws.onerror = (e) => console.error('Sidebar WS Error:', e);

    return () => {
      ws.close();
    };
  }, [cryptos, selectedCoin]);

  // AUTOMATIC TRIGGER LOGIC FOR LIMIT ORDERS AND SL/TP LEVELS
  useEffect(() => {
    if (!livePrices || Object.keys(livePrices).length === 0) return;

    // 1. Check pending limit orders for all coins
    const pendingOrders = orders.filter(o => o.status === 'PENDING');
    pendingOrders.forEach(async (order) => {
      const coinSymbol = order.symbol.replace('USDT', '').toLowerCase();
      const currentPrice = livePrices[coinSymbol];
      if (!currentPrice) return;

      if (triggeringInFlight.current.has(order._id)) return;

      let triggered = false;
      if (order.side === 'BUY' && currentPrice <= order.price) {
        triggered = true;
      } else if (order.side === 'SELL' && currentPrice >= order.price) {
        triggered = true;
      }

      if (triggered) {
        triggeringInFlight.current.add(order._id);
        try {
          console.log(`Triggering limit order for ${order.symbol} at ${currentPrice} (Limit: ${order.price})`);
          await triggerLimitOrderAPI(order._id, currentPrice, authHeaders);
          await Promise.all([fetchPortfolio(), fetchOrders()]);
        } catch (err) {
          console.error('Trigger limit order API error:', err);
        } finally {
          triggeringInFlight.current.delete(order._id);
        }
      }
    });

    // 2. Check SL/TP levels for all active positions (holdings)
    portfolio.holdings?.forEach(async (holding) => {
      const coinSymbol = getBaseSymbol(holding.symbol);
      const currentPrice = livePrices[coinSymbol];
      if (!currentPrice) return;

      // Don't auto-trigger right after user manually sets SL/TP
      if (recentlyUpdatedSLTP.current.has(holding.coinId)) return;

      // Skip if a trigger for this holding is already in-flight
      if (triggeringInFlight.current.has(`holding:${holding.coinId}`)) return;

      let triggerType = null;
      if (holding.stopLoss && currentPrice <= holding.stopLoss) {
        triggerType = 'SL';
      } else if (holding.takeProfit && currentPrice >= holding.takeProfit) {
        triggerType = 'TP';
      }

      if (triggerType) {
        triggeringInFlight.current.add(`holding:${holding.coinId}`);
        try {
          console.log(`Triggering ${triggerType} on holding ${holding.symbol} at ${currentPrice} (SL: ${holding.stopLoss}, TP: ${holding.takeProfit})`);
          await triggerSLTPAPI(holding.coinId, triggerType, currentPrice, authHeaders);
          await Promise.all([fetchPortfolio(), fetchOrders()]);
        } catch (err) {
          console.error('Trigger SL/TP API error:', err);
        } finally {
          triggeringInFlight.current.delete(`holding:${holding.coinId}`);
        }
      }
    });
  }, [livePrices, orders, portfolio, fetchOrders, fetchPortfolio, authHeaders]);

  // Calculate Unrealized PNL
  const getUnrealizedPnL = () => {
    let totalValue = 0;
    let totalCost = 0;
    
    portfolio.holdings?.forEach(holding => {
      const coinSym = getBaseSymbol(holding.symbol);
      const markPrice = livePrices[coinSym] || holding.avgEntryPrice;
      totalCost += holding.quantity * holding.avgEntryPrice;
      totalValue += holding.quantity * markPrice;
    });

    const pnl = totalValue - totalCost;
    const pnlPercent = totalCost > 0 ? (pnl / totalCost) * 100 : 0;
    return { pnl, pnlPercent, totalValue };
  };

  const { pnl: unrealizedPnL, pnlPercent: unrealizedPnLPercent, totalValue: holdingsValue } = getUnrealizedPnL();
  const accountValue = portfolio.balance + holdingsValue;

  // Handle Quick Size Percentage Buttons
  const handlePercentageSelect = (percent) => {
    if (!selectedCoin || !livePrice) return;
    
    if (side === 'BUY') {
      const priceToUse = orderType === 'LIMIT' ? parseFloat(limitPrice) : livePrice;
      if (!priceToUse || isNaN(priceToUse)) return;
      const budget = portfolio.balance * percent;
      const calculatedQty = budget / priceToUse;
      setQuantity(calculatedQty.toFixed(6));
    } else {
      const holding = portfolio.holdings?.find(h => h.coinId === selectedCoin.id);
      if (!holding) {
        setQuantity('0');
        return;
      }
      const calculatedQty = holding.quantity * percent;
      setQuantity(calculatedQty.toFixed(6));
    }
  };

  // Submit Order Form
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!selectedCoin || !quantity || !limitPrice) {
      setFormError('Please fill in all order details.');
      return;
    }

    setTradeLoading(true);
    try {
      const orderPayload = {
        coinId: selectedCoin.id,
        symbol: getBinanceSymbol(selectedCoin.symbol),
        type: orderType,
        side,
        price: orderType === 'LIMIT' ? parseFloat(limitPrice) : livePrice,
        quantity: parseFloat(quantity),
      };

      if (useSLTP) {
        if (stopLoss) orderPayload.stopLoss = parseFloat(stopLoss);
        if (takeProfit) orderPayload.takeProfit = parseFloat(takeProfit);
      }

      const { data } = await placeOrderAPI(orderPayload, authHeaders);
      setFormSuccess(data.message);
      toast.success(data.message || 'Order placed successfully!');
      setQuantity('');
      setStopLoss('');
      setTakeProfit('');
      setUseSLTP(false);
      
      // Refresh portfolio and orders
      await Promise.all([fetchPortfolio(), fetchOrders()]);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to place order.');
    } finally {
      setTradeLoading(false);
    }
  };

  // Cancel Pending Limit Order
  const handleCancelOrder = async (orderId) => {
    try {
      await cancelOrderAPI(orderId, authHeaders);
      await Promise.all([fetchPortfolio(), fetchOrders()]);
      toast.info('Order cancelled');
    } catch (err) {
      console.error('Error cancelling order:', err);
    }
  };

  // Close Active Position (Market Sell)
  const handleClosePosition = async (holding) => {
    const coinSymbol = getBaseSymbol(holding.symbol);
    const currentPrice = livePrices[coinSymbol] || holding.avgEntryPrice;
    try {
      await closePositionAPI(holding.coinId, currentPrice, authHeaders);
      await Promise.all([fetchPortfolio(), fetchOrders()]);
      toast.success('Position closed successfully');
    } catch (err) {
      console.error('Error closing position:', err);
    }
  };

  // Edit and Save Inline SL/TP
  const startEditingSLTP = (holding) => {
    setEditingSLTP(holding.coinId);
    setEditSLVal(holding.stopLoss ? holding.stopLoss.toString() : '');
    setEditTPVal(holding.takeProfit ? holding.takeProfit.toString() : '');
  };

  const saveInlineSLTP = async (holding) => {
    try {
      await updateSLTPAPI(
        holding.coinId, 
        editSLVal === '' ? null : parseFloat(editSLVal), 
        editTPVal === '' ? null : parseFloat(editTPVal), 
        authHeaders
      );
      setEditingSLTP(null);
      recentlyUpdatedSLTP.current.add(holding.coinId);
      setTimeout(() => {
        recentlyUpdatedSLTP.current.delete(holding.coinId);
      }, 3000);
      fetchPortfolio();
      toast.success('SL/TP updated');
    } catch (err) {
      console.error('Error saving SL/TP:', err);
    }
  };

  // Reset Demo Account
  const handleToggleIndicator = (key) => {
    setActiveIndicators(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearIndicators = () => {
    setActiveIndicators({ SMA: false, EMA: false, RSI: false, BollingerBands: false });
  };

  const handleResetAccount = async () => {
    if (window.confirm('Are you sure you want to reset your portfolio and transaction history back to $10,000?')) {
      try {
        const { data } = await resetAccountAPI(authHeaders);
        setPortfolio(data.portfolio);
        setOrders([]);
        toast.success('Demo account reset to $10,000');
      } catch (err) {
        console.error('Error resetting demo account:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="trading-terminal skeleton-terminal">
        <div className="terminal-header" style={{ display: 'flex', alignItems: 'center', padding: '0 20px', minHeight: '60px' }}>
           {/* Brand */}
           <div className="skeleton-box" style={{ width: 150, height: 28, borderRadius: 4 }}></div>
           
           {/* Balances */}
           <div style={{ display: 'flex', gap: 24, marginLeft: 'auto' }}>
             <div className="skeleton-box" style={{ width: 100, height: 32, borderRadius: 4 }}></div>
             <div className="skeleton-box" style={{ width: 100, height: 32, borderRadius: 4 }}></div>
             <div className="skeleton-box" style={{ width: 100, height: 32, borderRadius: 4 }}></div>
           </div>
           
           {/* Buttons / Profile */}
           <div style={{ display: 'flex', gap: 12, marginLeft: 24 }}>
             <div className="skeleton-box" style={{ width: 80, height: 32, borderRadius: 4 }}></div>
             <div className="skeleton-box" style={{ width: 32, height: 32, borderRadius: '50%' }}></div>
           </div>
        </div>

        <div className="terminal-body">
          <main className="center-panel" style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-color)' }}>
            {/* Chart Section Skeleton */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, margin: 1 }}>
              <div style={{ height: 40, borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12 }}>
                <div className="skeleton-box" style={{ width: 40, height: 20, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: 40, height: 20, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: 40, height: 20, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: 80, height: 20, borderRadius: 4, marginLeft: 'auto' }}></div>
              </div>
              <div className="skeleton-box" style={{ flex: 1, margin: 8, borderRadius: 4 }}></div>
            </div>
            
            {/* Positions Panel Skeleton */}
            <div style={{ height: 280, margin: 1, marginTop: 0, borderTop: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: 16, padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}>
                <div className="skeleton-box" style={{ width: 100, height: 24, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: 100, height: 24, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: 100, height: 24, borderRadius: 4 }}></div>
              </div>
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="skeleton-box" style={{ width: '100%', height: 30, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: '100%', height: 30, borderRadius: 4 }}></div>
                <div className="skeleton-box" style={{ width: '100%', height: 30, borderRadius: 4 }}></div>
              </div>
            </div>
          </main>

          {/* Order Entry Skeleton */}
          <aside className="order-panel" style={{ width: 320, display: 'flex', flexDirection: 'column', padding: 16, gap: 16 }}>
             <div style={{ display: 'flex', gap: 8 }}>
               <div className="skeleton-box" style={{ flex: 1, height: 36, borderRadius: 4 }}></div>
               <div className="skeleton-box" style={{ flex: 1, height: 36, borderRadius: 4 }}></div>
             </div>
             <div className="skeleton-box" style={{ width: '100%', height: 40, borderRadius: 4 }}></div>
             <div className="skeleton-box" style={{ width: '100%', height: 40, borderRadius: 4 }}></div>
             
             {/* Percent sizes */}
             <div style={{ display: 'flex', gap: 8 }}>
               <div className="skeleton-box" style={{ flex: 1, height: 24, borderRadius: 4 }}></div>
               <div className="skeleton-box" style={{ flex: 1, height: 24, borderRadius: 4 }}></div>
               <div className="skeleton-box" style={{ flex: 1, height: 24, borderRadius: 4 }}></div>
               <div className="skeleton-box" style={{ flex: 1, height: 24, borderRadius: 4 }}></div>
             </div>

             <div className="skeleton-box" style={{ width: '100%', height: 48, borderRadius: 4, marginTop: 'auto' }}></div>
          </aside>
        </div>
        
        <div className="skeleton-overlay-text">
          <div className="industry-loader"></div>
          <h3 style={{ marginBottom: 8, color: 'var(--text-primary)' }}>Establishing Secure Connection</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0 }}>Syncing live market data for you</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 8 }}>Its Just a matter of few seconds (approx 10-15s)... </p>
        </div>
      </div>
    );
  }

  // Get active holding details for chart target lines display
  const activeHolding = selectedCoin ? portfolio.holdings?.find(h => h.coinId === selectedCoin.id) : null;
  const currentHoldingQty = activeHolding ? activeHolding.quantity : 0;

  return (
    <div className="trading-terminal">
      <TerminalHeader 
        user={user}
        logout={logout}
        selectedCoin={selectedCoin}
        livePrices={livePrices}
        accountValue={accountValue}
        portfolio={portfolio}
        unrealizedPnL={unrealizedPnL}
        unrealizedPnLPercent={unrealizedPnLPercent}
        handleResetAccount={handleResetAccount}
        priceFlash={priceFlash}
        onOpenCoinSelector={() => setIsCoinSelectorOpen(true)}
        onOpenPnLCalendar={() => setIsPnLCalendarOpen(true)}
      />

      <PnLCalendarModal 
        isOpen={isPnLCalendarOpen} 
        onClose={() => setIsPnLCalendarOpen(false)} 
      />

      <CoinSelectorModal 
        isOpen={isCoinSelectorOpen}
        onClose={() => setIsCoinSelectorOpen(false)}
        cryptos={cryptos}
        livePrices={livePrices}
        selectedCoin={selectedCoin}
        setSelectedCoin={setSelectedCoin}
        setLimitPrice={setLimitPrice}
        setFormError={setFormError}
        setFormSuccess={setFormSuccess}
        priceFlash={priceFlash}
      />

      <div className="terminal-body">
        <main className="center-panel">
          <ChartSection 
            selectedCoin={selectedCoin}
            timeframe={timeframe}
            setTimeframe={setTimeframe}
            setLivePrice={setLivePrice}
            activeHolding={activeHolding}
            orders={orders}
            chartType={chartType}
            setChartType={setChartType}
            showVolume={showVolume}
            setShowVolume={setShowVolume}
            activeIndicators={activeIndicators}
            handleToggleIndicator={handleToggleIndicator}
            handleClearIndicators={handleClearIndicators}
          />

          <PositionsPanel 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            portfolio={portfolio}
            orders={orders}
            livePrices={livePrices}
            editingSLTP={editingSLTP}
            setEditingSLTP={setEditingSLTP}
            editSLVal={editSLVal}
            setEditSLVal={setEditSLVal}
            editTPVal={editTPVal}
            setEditTPVal={setEditTPVal}
            startEditingSLTP={startEditingSLTP}
            saveInlineSLTP={saveInlineSLTP}
            handleClosePosition={handleClosePosition}
            handleCancelOrder={handleCancelOrder}
          />
        </main>

        <OrderEntry 
          selectedCoin={selectedCoin}
          livePrice={livePrice}
          portfolio={portfolio}
          side={side}
          setSide={setSide}
          orderType={orderType}
          setOrderType={setOrderType}
          limitPrice={limitPrice}
          setLimitPrice={setLimitPrice}
          quantity={quantity}
          setQuantity={setQuantity}
          useSLTP={useSLTP}
          setUseSLTP={setUseSLTP}
          stopLoss={stopLoss}
          setStopLoss={setStopLoss}
          takeProfit={takeProfit}
          setTakeProfit={setTakeProfit}
          handlePercentageSelect={handlePercentageSelect}
          handlePlaceOrder={handlePlaceOrder}
          tradeLoading={tradeLoading}
          formError={formError}
          setFormError={setFormError}
          formSuccess={formSuccess}
          setFormSuccess={setFormSuccess}
          currentHoldingQty={currentHoldingQty}
        />
      </div>
    </div>
  );
};

export default TradingTerminal;
